import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useJsonLd } from '@/hooks/useJsonLd';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const Services = () => {
  const services = [
    {
      // Featured — the fixed-fee Mid-Year Business Health Check funnel.
      name: 'Mid-Year Business Health Check',
      tagline: 'Know exactly where your business stands — before year-end surprises.',
      description: 'A fixed-fee CPA diagnostic for business owners who want a clear mid-year picture. We catch up your books (Jan–Jun), prepare real financial statements, project your tax exposure, and review payroll, sales-tax, and registration compliance — then hand you a written findings report and a 60-minute strategy meeting.',
      highlights: [
        'Bookkeeping catch-up for January through June',
        'Financial statements you can actually act on',
        'Mid-year tax projection with no year-end surprises',
        'Payroll, sales-tax & registration compliance review',
        'Written findings report + 60-minute strategy meeting'
      ],
      cta: 'See If You Qualify',
      link: '/business-health-check',
      featured: true,
      featuredNote: 'Limited to 25 engagements this quarter · Book before September 30',
    },
    {
      name: 'Accounting & Payroll',
      tagline: 'Clarity without chaos.',
      description: 'Most long-term KGOB relationships begin here. When your books are clean and current, everything else becomes clearer and more effective. We provide CPA-led monthly bookkeeping and payroll support designed for growing businesses.',
      highlights: [
        'Monthly bookkeeping and reconciliations',
        'QuickBooks Online setup, cleanup, and customized chart of accounts',
        'Monthly financial statements you can actually read and use',
        'Support for sales tax tracking and filings where needed',
        'Payroll administration, integrated with your accounting'
      ],
      cta: 'Explore Accounting & Payroll',
      link: '/services/accounting-payroll'
    },
    {
      name: 'Tax Services',
      tagline: 'Compliance handled. Strategy built in.',
      description: 'We provide comprehensive tax preparation and planning for both individuals and businesses who want more than a once-a-year filing. Our goal is to integrate your tax work with your books and overall strategy.',
      highlights: [
        'Individual and business tax preparation',
        'Multi-jurisdiction and multi-state filings',
        'Proactive tax planning and strategy conversations',
        'Estimated tax payment and cash-flow planning',
        'Coordination with your bookkeeping'
      ],
      cta: 'Explore Tax Services',
      link: '/services/tax'
    },
    {
      name: 'IRS Representation & Relief',
      tagline: 'Calm, clarity, and representation when it matters most.',
      description: 'When facing IRS or state notices, you should not have to decode them alone. KGOB steps in to interpret what the notice means, respond on your behalf, and guide you through next steps.',
      highlights: [
        'IRS and state notices, letters, and audit responses',
        'Transcript retrieval and plain-language explanations',
        'Assistance with payment plans and penalty relief',
        'Coordination with monitoring tools for ongoing peace of mind'
      ],
      cta: 'Get IRS Help',
      link: '/services/irs-relief'
    },
    {
      name: 'Entity Creation & Restructuring',
      tagline: 'Start right. Structure for savings and growth.',
      description: 'The way your business is structured has long-term implications for taxes, liability, and exit planning. We help you choose and implement the right entity design.',
      highlights: [
        'Guidance on entity selection (LLC, S corporation, partnership)',
        'Support with EIN and basic state tax registrations',
        'S-election support where appropriate',
        'Coordination with your attorney',
        'Clear explanation of ongoing filing requirements'
      ],
      cta: 'Compare Entity Options',
      link: '/services/entity',
      disclaimer: 'State and filing fees are always billed separately.'
    },
    {
      name: 'Business Valuation & Growth',
      tagline: 'Know your value. Then make it grow.',
      description: 'For many owners, their business is their largest asset. We help you understand where your business stands today and which levers will make the biggest difference over time.',
      highlights: [
        'Estimated business value snapshots',
        'Exit and succession readiness conversations',
        'Benchmarking and value-driver scorecards',
        <>Integration with <a href="https://letstalkgrowth.com" target="_blank" rel="noopener noreferrer" className="text-[#0B2240] font-medium underline decoration-[#D4AF37] underline-offset-2 hover:text-[#D4AF37]" data-testid="services-letstalkgrowth-link">LetsTalkGrowth.com</a> tools</>
      ],
      cta: 'See Growth & Valuation Services',
      link: '/services/valuation'
    },
    {
      name: 'Strategic Consulting',
      tagline: 'When the numbers need to drive the decision.',
      description: 'Some questions involve timing, trade-offs, and real-world impact. Our strategic consulting is designed for those moments when you need a calm, financially grounded perspective.',
      highlights: [
        'Focused consultations on tax or business questions',
        'Mergers, acquisitions, and expansion readiness',
        'Cash-flow, profitability, and scenario modeling',
        'Coordination with your attorney and other advisors'
      ],
      cta: 'Request a Strategy Session',
      link: '/services/consulting'
    },
    {
      name: 'Financial Tools & Resources',
      tagline: 'Simple, self-serve wins without waiting.',
      description: 'For those who like to get started on their own time, we offer curated tools and resources through our sister platforms. These complement our full services or give you a starting point.',
      highlights: [
        'Tax savings and planning toolkits',
        'Federal tax planner spreadsheets',
        'Tax strategy guides for business owners',
        'Access to AskSara and BizHub resources'
      ],
      cta: 'Explore Tools & Resources',
      link: '/services/tools'
    },
    {
      // 8th service — the paid Ask-a-Question / Quick Consult product.
      // Distinct from the 7 above: it's a transactional one-off paid consult
      // ($95-$240), not a recurring engagement. Visually differentiated by
      // the "FROM $95" badge so a browsing user can immediately see this is
      // a lower-friction entry point than full client engagement.
      name: 'Ask a CPA',
      tagline: 'A focused CPA consult, no long-term commitment.',
      description: 'Bring your exact tax situation to a licensed NC CPA for a focused 30 to 90-minute session. Three flat-fee tiers, your choice. You walk away with clear options and a next step, whether that\'s working with us long-term or handling it on your own.',
      highlights: [
        'Quick Question, 30 minutes',
        'Deep Dive, 60 minutes',
        'Strategy Session, 90 minutes',
        'Browse a library of 2,800+ canonical tax questions',
        'Or describe your own situation in your own words'
      ],
      cta: 'Browse questions, ask yours',
      link: '/get-your-answer',
    }
  ];

  useJsonLd('services-list', {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "KGOB CPAs & Advisors Services",
    "url": "https://kgob.com/services",
    "itemListElement": services.map((s, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Service",
        "name": s.name,
        "description": s.tagline,
        "url": `https://kgob.com${s.link}`,
        "provider": { "@type": "Organization", "name": "Kohari Gonzalez Oneyear & Brown", "url": "https://kgob.com" }
      }
    }))
  });

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>CPA Services | Tax, Accounting, Valuation & Advisory | KGOB</title>
        <meta name="description" content="Explore KGOB services: tax planning and preparation, accounting and payroll, business valuation, entity structuring, IRS resolution, and strategic consulting for growing businesses." />
        <link rel="canonical" href="https://kgob.com/services" />
        <meta property="og:title" content="Services | KGOB CPAs & Advisors" />
        <meta property="og:description" content="Explore KGOB services: tax planning and preparation, accounting and payroll, business valuation, entity structuring, IRS resolution, and strategic consulting for growing businesses." />
        <meta property="og:url" content="https://kgob.com/services" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                What We Do and Why It Matters.
              </h1>
              
              <p className="text-xl text-[#F7F6F2]/90 mb-6 max-w-3xl mx-auto">
                At KGOB, we serve leaders, entrepreneurs, and families who want proactive insight. Our role is to bring calm, clarity, and a long-term plan.
              </p>
              
              <p className="text-base text-[#D4AF37] mb-10">
                If you are looking for a firm that quietly handles the details while helping you make better decisions, you are in the right place.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button data-testid="services-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="services-link-1" to="/services/selector">Start Here</Link>
                </Button>
                <Button data-testid="services-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <a data-testid="services-a-1" href="https://app.kgob.com/book-consultation?source=services-hero">Request Consult</a>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-4">
                Our Core Service Areas
              </h2>
            </div>

            <div className="space-y-8">
              {services.map((service, index) => (
                <Card
                  key={index}
                  data-testid={service.featured ? 'service-card-featured-bhc' : undefined}
                  className={`p-10 border-2 hover:shadow-xl ${
                    service.featured
                      ? 'relative border-[#D4AF37] bg-[#FDFBF4] shadow-[0_8px_30px_rgba(212,175,55,0.15)]'
                      : 'border-[#E1DFD7]'
                  }`}
                >
                  {service.featured && (
                    <span className="absolute -top-3.5 left-8 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0B2240] text-[#D4AF37] text-[10px] font-semibold tracking-[0.16em] uppercase">
                      Featured · Limited Availability
                    </span>
                  )}
                  <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-2">
                      <div className="flex items-start gap-3 mb-2 flex-wrap">
                        <h3 className="font-['Playfair_Display'] text-3xl text-[#0B2240]">
                          {service.name}
                        </h3>
                        {service.badge && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-[0.14em] uppercase border border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B2240] self-center" data-testid={`service-card-badge-${index}`}>
                            {service.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#D4AF37] italic mb-4">{service.tagline}</p>
                      <p className="text-[#4C5A70] mb-6">{service.description}</p>
                      
                      <div className="space-y-2">
                        {(service.highlights || []).map((h, i) => (
                          <div key={i} className="flex gap-3">
                            <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                            <span className="text-sm text-[#4C5A70]">{h}</span>
                          </div>
                        ))}
                      </div>
                      
                      {service.disclaimer && (
                        <p className="text-xs text-[#4C5A70]/70 italic mt-4">{service.disclaimer}</p>
                      )}
                    </div>
                    
                    <div className="flex flex-col justify-center">
                      <Button data-testid="services-button-1" asChild className="w-full h-12 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                        <Link data-testid="services-link-1" to={service.link}>{service.cta} →</Link>
                      </Button>
                      {service.featuredNote && (
                        <p className="text-xs text-[#4C5A70]/80 text-center mt-3" data-testid="service-card-featured-note">
                          {service.featuredNote}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Services;
